import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { LuTriangleAlert, LuCheck } from "react-icons/lu";
import AppModal from "./AppModal";
import { getToken, getUserInfo } from "../../../../common/environment_config.es6";
import { CREDIT_REFUSAL } from "../../../utils/insufficientBalance";
import "./_insufficientBalanceModal.scss";

/**
 * Shown when the middleware refuses a paid operation. Two causes, one popup:
 *
 *   kind "balance" — the organization is out of credits: contact aidea.
 *   kind "team"    — the user's team monthly budget is used up. aidea support
 *                    cannot raise that (the customer's own org admin sets it
 *                    in console settings), so the contact form is not offered;
 *                    the text says who to ask and when the budget renews.
 */
const InsufficientBalanceModal = ({
  show,
  onClose,
  requestedAmount,
  currentBalance = 0,
  unit,
  apiUrl,
  title,
  description,
  kind = CREDIT_REFUSAL.BALANCE,
  teamName = null,
  resetsAt = null,
}) => {
  const isTeam = kind === CREDIT_REFUSAL.TEAM;
  const resolvedUnit = unit || i18n.t("InsufficientBalance.UnitCredits") || "kredi";
  const resetsOn = resetsAt
    ? new Date(resetsAt).toLocaleDateString(i18n.language || undefined, {
        day: "numeric",
        month: "long",
      })
    : "";
  const resolvedTitle = isTeam
    ? title || i18n.t("InsufficientBalance.TeamTitle") || "Takım bütçeniz doldu"
    : title || i18n.t("InsufficientBalance.Title") || "Yetersiz Bakiye";
  const resolvedDescription = isTeam
    ? description ||
      i18n.t("InsufficientBalance.TeamDescription", {
        team: teamName || i18n.t("InsufficientBalance.YourTeam") || "Takımınız",
        date: resetsOn,
      }) ||
      "Takımınızın bu ayki kredi bütçesi bu işlem için yetmiyor. Organizasyon yöneticinizle görüşün."
    : description ||
      i18n.t("InsufficientBalance.Description") ||
      "Krediniz bitti. Yeni kredi için bizimle iletişime geçin.";

  const [expanded, setExpanded] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!show) {
      setExpanded(false);
      setMessage("");
      setSubmitting(false);
      setSubmitError(null);
      setSent(false);
    }
  }, [show]);

  const formatAmount = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    return value;
  };

  const resolveSupportUrl = () => {
    const base =
      apiUrl ||
      process.env.MW_API_URL ||
      (typeof window !== "undefined" ? window.ideaLibraryRootUrl : null) ||
      "";
    if (!base) return "/api/support";
    return `${base.endsWith("/") ? base : `${base}/`}api/support`;
  };

  const handleContactClick = () => {
    if (sent) return;
    setExpanded((prev) => !prev);
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    const trimmed = message.trim();
    if (!trimmed || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      const { name, email } = getUserInfo();
      const res = await fetch(resolveSupportUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          name: name || email || "",
          surname: "",
          email: email || "",
          message: trimmed,
        }),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      setSent(true);
      setExpanded(false);
      setMessage("");
    } catch (err) {
      console.error("[InsufficientBalanceModal] support request error:", err);
      setSubmitError(
        i18n.t("InsufficientBalance.SubmitError") ||
          "Mesajınız gönderilirken bir hata oluştu. Lütfen tekrar deneyin."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppModal
      show={show}
      onClose={onClose}
      size="md"
      maxWidth={480}
      closeButton={false}
      showSceneToggle={false}
      className="insufficient-balance-modal"
      renderHeader={() => null}
      footer={null}
    >
      <div className="insufficient-balance-modal__body">
        <div className="insufficient-balance-modal__icon-wrap">
          <LuTriangleAlert
            aria-hidden="true"
            className="insufficient-balance-modal__icon"
            size={28}
          />
        </div>

        <div className="insufficient-balance-modal__title">{resolvedTitle}</div>
        <div className="insufficient-balance-modal__description">{resolvedDescription}</div>

        <div className="insufficient-balance-modal__info-card">
          <div className="insufficient-balance-modal__info-row">
            <span className="insufficient-balance-modal__info-label">
              {i18n.t("InsufficientBalance.RequestedAmount") || "İstenen süre"}
            </span>
            <span className="insufficient-balance-modal__info-value insufficient-balance-modal__info-value--accent">
              {formatAmount(requestedAmount)} {resolvedUnit}
            </span>
          </div>
          <div className="insufficient-balance-modal__info-row">
            <span className="insufficient-balance-modal__info-label">
              {isTeam
                ? i18n.t("InsufficientBalance.TeamRemaining") || "Kalan takım bütçesi"
                : i18n.t("InsufficientBalance.CurrentBalance") || "Mevcut bakiye"}
            </span>
            <span className="insufficient-balance-modal__info-value">
              {formatAmount(currentBalance)} {resolvedUnit}
            </span>
          </div>
        </div>

        <div className="insufficient-balance-modal__actions">
          {!isTeam && (
            <button
              type="button"
              className="insufficient-balance-modal__btn insufficient-balance-modal__btn--primary"
              onClick={handleContactClick}
              disabled={sent}
              aria-expanded={expanded}
            >
              {i18n.t("InsufficientBalance.ContactUs") || "Bizimle İletişime Geçin"}
            </button>
          )}
          <button
            type="button"
            className="insufficient-balance-modal__btn insufficient-balance-modal__btn--ghost"
            onClick={onClose}
          >
            {i18n.t("InsufficientBalance.Close") || "Kapat"}
          </button>
        </div>

        {expanded && !sent && (
          <div className="insufficient-balance-modal__compose">
            <label
              htmlFor="insufficient-balance-message"
              className="insufficient-balance-modal__compose-label"
            >
              {i18n.t("InsufficientBalance.MessageLabel") || "Mesajınız"}
            </label>
            <textarea
              id="insufficient-balance-message"
              className="insufficient-balance-modal__textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                i18n.t("InsufficientBalance.MessagePlaceholder") || "Mesajınızı buraya yazın..."
              }
              rows={5}
              disabled={submitting}
              autoFocus
            />

            {submitError && <div className="insufficient-balance-modal__error">{submitError}</div>}

            <button
              type="button"
              className="insufficient-balance-modal__btn insufficient-balance-modal__btn--primary insufficient-balance-modal__send-btn"
              onClick={handleSubmit}
              disabled={!message.trim() || submitting}
            >
              {submitting
                ? i18n.t("InsufficientBalance.Sending") || "Gönderiliyor..."
                : i18n.t("InsufficientBalance.Send") || "Gönder"}
            </button>
          </div>
        )}

        {sent && (
          <div className="insufficient-balance-modal__sent">
            <LuCheck className="insufficient-balance-modal__sent-icon" size={16} />
            <span>
              {i18n.t("InsufficientBalance.MessageSentDescription") ||
                "Talebiniz ekibimize iletildi. En kısa sürede size dönüş yapacağız."}
            </span>
          </div>
        )}
      </div>
    </AppModal>
  );
};

InsufficientBalanceModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  requestedAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentBalance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  unit: PropTypes.string,
  apiUrl: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  kind: PropTypes.oneOf([CREDIT_REFUSAL.BALANCE, CREDIT_REFUSAL.TEAM]),
  teamName: PropTypes.string,
  resetsAt: PropTypes.string,
};

export default InsufficientBalanceModal;
